'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchTours } from '@/actions';
import TourCard from '@/components/search/TourCard';
import { useCurrency } from '@/lib/context/CurrencyContext';
import { QUERY_KEYS } from '@/lib/query-keys';
import { TActivity, TLocation, TTour } from '@/types';

const PAGE_SIZE = 20;

interface TourSearchResultsProps {
  initialTours: TTour[];
  totalCount: number;
  searchBody: Record<string, unknown>;
  activity: TActivity | undefined;
  location: TLocation | undefined;
}

export function TourSearchResults({
  initialTours,
  totalCount,
  searchBody,
  activity,
  location,
}: TourSearchResultsProps) {
  const { currentCurrency } = useCurrency();
  const bodyKey = JSON.stringify(searchBody);

  // Seed SSR data only when it matches the selected currency — never show USD under EUR.
  const canSeedInitial =
    initialTours.length === 0 || initialTours[0]?.currency === currentCurrency;

  const { data, isPending } = useQuery({
    queryKey: QUERY_KEYS.tourSearch(currentCurrency, bodyKey),
    queryFn: () =>
      fetchTours({
        body: searchBody,
        params: { page: 0, size: PAGE_SIZE },
        currency: currentCurrency,
      }),
    initialData: canSeedInitial ? { tours: initialTours, total: totalCount } : undefined,
    // Avoid remount SA refetch on navigation while RSC still provides fresh seed data.
    staleTime: 5 * 60 * 1000,
  });

  // Pagination only — page-0 price data comes from React Query.
  const [extraTours, setExtraTours] = useState<TTour[]>([]);
  const [page, setPage] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(
    (canSeedInitial ? initialTours.length : 0) >= PAGE_SIZE
  );
  const observerTarget = useRef<HTMLDivElement | null>(null);
  const isLoadingMoreRef = useRef(false);

  useEffect(() => {
    setExtraTours([]);
    setPage(0);
    setHasMore((data?.tours.length ?? 0) >= PAGE_SIZE);
  }, [currentCurrency, bodyKey, data?.tours.length]);

  const loadMore = useCallback(async () => {
    if (isLoadingMoreRef.current || !hasMore) return;

    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const nextPage = page + 1;
      const { tours: nextTours } = await fetchTours({
        body: searchBody,
        params: { page: nextPage, size: PAGE_SIZE },
        currency: currentCurrency,
      });

      setExtraTours((prev) => [...prev, ...nextTours]);
      setPage(nextPage);
      setHasMore(nextTours.length >= PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load more tours:', error);
      setHasMore(false);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [page, hasMore, searchBody, currentCurrency]);

  useEffect(() => {
    const sentinel = observerTarget.current;
    if (!sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting || isLoadingMoreRef.current || !hasMore) return;
        loadMore();
      },
      { rootMargin: '200px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore]);

  const activityName = activity?.name ?? 'All activities';
  const locationSuffix = location ? ` in ${location.name}` : ', anywhere';
  const page0Tours = data?.tours ?? [];
  const tours = [...page0Tours, ...extraTours];
  const displayTotal = data?.total ?? totalCount;

  return (
    <div className="flex flex-col gap-8 lg:gap-[44px] font-medium">
      <div className="tour-grid-title">
        <h1 role="title" className="h2 inline">
          {activityName}
          {locationSuffix}.
        </h1>
        <span className="h2">
          {' '}
          <span className="text-dimmer">We found {displayTotal} activities</span> 🔍
        </span>
      </div>

      {isPending && !data ? (
        <div className="flex justify-center items-center gap-3 py-16">
          <div
            className="animate-spin rounded-full border-2 border-gray-500 border-t-transparent"
            style={{ width: 24, height: 24 }}
            aria-hidden
          />
          <span className="text-dimmer">Updating prices…</span>
        </div>
      ) : (
        <div className="grid grid-cols-auto-fill-310 gap-x-4 gap-y-8 lg:gap-y-24">
          {tours.map((tour) => (
            <TourCard key={tour.id} tour={tour} />
          ))}
        </div>
      )}

      {hasMore && <div ref={observerTarget} className="h-4" aria-hidden />}
      {isLoadingMore && (
        <div className="flex justify-center items-center gap-3 py-8">
          <div
            className="animate-spin rounded-full border-2 border-gray-500 border-t-transparent"
            style={{ width: 24, height: 24 }}
            aria-hidden
          />
          <span className="text-dimmer">Loading more...</span>
        </div>
      )}
    </div>
  );
}
